/**
 * Performance Monitoring Utilities
 *
 * Provides Web Vitals tracking and performance metrics collection for monitoring
 * application health and user experience. Tracks:
 * - Largest Contentful Paint (LCP) - Loading Performance
 * - First Input Delay (FID) - Interactivity
 * - Cumulative Layout Shift (CLS) - Visual Stability
 * - Page load time and resource metrics
 */

export interface WebVitalsMetric {
  name: 'LCP' | 'FID' | 'CLS' | 'TTFB' | 'FCP';
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta?: number;
  id?: string;
  entries?: PerformanceEntry[];
  navigationType?: NavigationType;
  attribution?: Record<string, any>;
}

export interface PerformanceMetrics {
  lcp?: number;
  fid?: number;
  cls?: number;
  ttfb?: number;
  fcp?: number;
  pageLoadTime?: number;
  resourceTimings?: PerformanceResourceTiming[];
}

export type NavigationType = 'navigate' | 'reload' | 'back-forward' | 'back-forward-cache' | 'restore';

type MetricHandler = (metric: WebVitalsMetric) => void;

const metricHandlers: MetricHandler[] = [];
const pageStartTime = performance.now();
let isMonitoringActive = false;

/**
 * Register a callback to receive Web Vitals metrics
 * @param handler Callback function for metrics
 */
export function onMetric(handler: MetricHandler): void {
  metricHandlers.push(handler);
}

/**
 * Send metric to all registered handlers
 */
function reportMetric(metric: WebVitalsMetric): void {
  metricHandlers.forEach((handler) => {
    try {
      handler(metric);
    } catch (error) {
      console.error('[PerformanceMonitoring] Error in metric handler:', error);
    }
  });
}

/**
 * Get Web Vitals rating based on thresholds
 * https://web.dev/vitals/
 */
function getRating(metricName: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const thresholds: Record<string, { good: number; poor: number }> = {
    LCP: { good: 2500, poor: 4000 },
    FID: { good: 100, poor: 300 },
    CLS: { good: 0.1, poor: 0.25 },
    TTFB: { good: 600, poor: 1800 },
    FCP: { good: 1800, poor: 3000 },
  };

  const threshold = thresholds[metricName];
  if (!threshold) return 'needs-improvement';

  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

/**
 * Monitor Largest Contentful Paint (LCP)
 * Measures loading performance - when the largest content element appears
 */
export function monitorLCP(): void {
  if (!('PerformanceObserver' in window)) {
    console.warn('[PerformanceMonitoring] PerformanceObserver not supported');
    return;
  }

  try {
    const observer = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const lastEntry = entries[entries.length - 1] as PerformanceEntry & {
        renderTime?: number;
        loadTime?: number;
        size?: number;
        id?: string;
        url?: string;
        element?: Element;
      };

      if (lastEntry) {
        const value = lastEntry.renderTime || lastEntry.loadTime || 0;

        reportMetric({
          name: 'LCP',
          value,
          rating: getRating('LCP', value),
          id: lastEntry.id,
          entries: [lastEntry],
        });
      }
    });

    observer.observe({ entryTypes: ['largest-contentful-paint'] });

    // Clean up after page unload
    window.addEventListener('pagehide', () => observer.disconnect(), true);
  } catch (error) {
    console.error('[PerformanceMonitoring] Error monitoring LCP:', error);
  }
}

/**
 * Monitor First Input Delay (FID)
 * Measures interactivity - delay between user input and response
 */
export function monitorFID(): void {
  if (!('PerformanceObserver' in window)) {
    return;
  }

  try {
    const observer = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();

      entries.forEach((entry: any) => {
        // Only take the first interaction
        if ('processingStart' in entry && entry.duration !== undefined) {
          const fid = entry.processingStart - entry.startTime;

          reportMetric({
            name: 'FID',
            value: fid,
            rating: getRating('FID', fid),
            entries: [entry],
          });
        }
      });
    });

    observer.observe({ entryTypes: ['first-input'] });

    // Clean up after page unload
    window.addEventListener('pagehide', () => observer.disconnect(), true);
  } catch (error) {
    console.error('[PerformanceMonitoring] Error monitoring FID:', error);
  }
}

/**
 * Monitor Cumulative Layout Shift (CLS)
 * Measures visual stability - unexpected layout shifts
 */
export function monitorCLS(): void {
  if (!('PerformanceObserver' in window)) {
    return;
  }

  try {
    let clsValue = 0;
    const observer = new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries()) {
        const layoutShift = entry as any;
        if (!layoutShift.hadRecentInput) {
          clsValue += layoutShift.value;

          reportMetric({
            name: 'CLS',
            value: clsValue,
            rating: getRating('CLS', clsValue),
            delta: layoutShift.value,
            entries: [layoutShift],
          });
        }
      }
    });

    observer.observe({ type: 'layout-shift', buffered: true });

    // Clean up after page unload
    window.addEventListener('pagehide', () => observer.disconnect(), true);
  } catch (error) {
    console.error('[PerformanceMonitoring] Error monitoring CLS:', error);
  }
}

/**
 * Monitor Time to First Byte (TTFB)
 * Measures server response time
 */
export function monitorTTFB(): void {
  try {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

    if (navigation) {
      const ttfb = navigation.responseStart - navigation.requestStart;

      reportMetric({
        name: 'TTFB',
        value: ttfb,
        rating: getRating('TTFB', ttfb),
        navigationType: navigation.type as NavigationType,
      });
    }
  } catch (error) {
    console.error('[PerformanceMonitoring] Error monitoring TTFB:', error);
  }
}

/**
 * Monitor First Contentful Paint (FCP)
 * Measures when first content appears
 */
export function monitorFCP(): void {
  if (!('PerformanceObserver' in window)) {
    return;
  }

  try {
    const observer = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const lastEntry = entries[entries.length - 1];

      reportMetric({
        name: 'FCP',
        value: lastEntry.startTime,
        rating: getRating('FCP', lastEntry.startTime),
        entries: [lastEntry],
      });
    });

    observer.observe({ entryTypes: ['paint'] });

    // Clean up after page unload
    window.addEventListener('pagehide', () => observer.disconnect(), true);
  } catch (error) {
    console.error('[PerformanceMonitoring] Error monitoring FCP:', error);
  }
}

/**
 * Get all available performance metrics
 */
export function getPerformanceMetrics(): PerformanceMetrics {
  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
  const paint = performance.getEntriesByType('paint');

  const metrics: PerformanceMetrics = {
    pageLoadTime: navigation?.loadEventEnd - navigation?.fetchStart,
    ttfb: navigation?.responseStart - navigation?.requestStart,
    resourceTimings: performance.getEntriesByType('resource') as PerformanceResourceTiming[],
  };

  // Add paint entries
  paint.forEach((entry) => {
    if (entry.name === 'first-contentful-paint') {
      metrics.fcp = entry.startTime;
    }
  });

  return metrics;
}

/**
 * Get detailed resource timing information
 */
export function getResourceTimings(): PerformanceResourceTiming[] {
  return performance.getEntriesByType('resource') as PerformanceResourceTiming[];
}

/**
 * Get slow resources (resources taking > threshold ms)
 */
export function getSlowResources(thresholdMs: number = 1000): PerformanceResourceTiming[] {
  return getResourceTimings().filter(
    (resource) => (resource.duration || 0) > thresholdMs
  );
}

/**
 * Initialize all Web Vitals monitoring
 */
export function startPerformanceMonitoring(): void {
  if (isMonitoringActive) {
    console.warn('[PerformanceMonitoring] Monitoring already active');
    return;
  }

  isMonitoringActive = true;

  // Monitor all Core Web Vitals
  monitorLCP();
  monitorFID();
  monitorCLS();
  monitorTTFB();
  monitorFCP();

  if (process.env.NODE_ENV === 'development') {
    console.debug('[PerformanceMonitoring] Performance monitoring started');
  }
}

/**
 * Stop performance monitoring
 */
export function stopPerformanceMonitoring(): void {
  isMonitoringActive = false;
  metricHandlers.length = 0;

  if (process.env.NODE_ENV === 'development') {
    console.debug('[PerformanceMonitoring] Performance monitoring stopped');
  }
}

/**
 * Clear all registered metric handlers
 */
export function clearMetricHandlers(): void {
  metricHandlers.length = 0;
}

/**
 * Log metrics to console (useful for debugging)
 */
export function logMetricsToConsole(metric: WebVitalsMetric): void {
  const color = {
    good: '#10b981',
    'needs-improvement': '#f59e0b',
    poor: '#ef4444',
  }[metric.rating];

  console.log(
    `%c[${metric.name}] ${metric.value.toFixed(2)}ms`,
    `color: ${color}; font-weight: bold;`,
    {
      rating: metric.rating,
      metric,
    }
  );
}

/**
 * Send metrics to external service (like Google Analytics)
 */
export function sendMetricsToService(
  metric: WebVitalsMetric,
  endpoint: string,
  customData?: Record<string, any>
): void {
  try {
    // Use navigator.sendBeacon for reliable delivery
    if (navigator.sendBeacon) {
      const payload = JSON.stringify({
        ...metric,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        ...customData,
      });

      navigator.sendBeacon(endpoint, payload);
    } else {
      // Fallback to fetch
      fetch(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          ...metric,
          timestamp: new Date().toISOString(),
          url: window.location.href,
          ...customData,
        }),
        keepalive: true,
      }).catch((error) => {
        console.error('[PerformanceMonitoring] Error sending metrics:', error);
      });
    }
  } catch (error) {
    console.error('[PerformanceMonitoring] Error in sendMetricsToService:', error);
  }
}

/**
 * Get session duration
 */
export function getSessionDuration(): number {
  return performance.now() - pageStartTime;
}

/**
 * Get page visibility state metrics
 */
export function getVisibilityMetrics(): {
  visibleTime: number;
  hiddenTime: number;
  visibilityChanges: number;
} {
  let visibleTime = 0;
  let hiddenTime = 0;
  let visibilityChanges = 0;
  let lastChangeTime = pageStartTime;

  const updateMetrics = () => {
    const now = performance.now();
    const duration = now - lastChangeTime;

    if (document.hidden) {
      hiddenTime += duration;
    } else {
      visibleTime += duration;
    }

    lastChangeTime = now;
  };

  // Track visibility changes
  document.addEventListener('visibilitychange', () => {
    updateMetrics();
    visibilityChanges++;
  });

  // Update metrics on page unload
  window.addEventListener('beforeunload', updateMetrics);

  return {
    visibleTime,
    hiddenTime,
    visibilityChanges,
  };
}
